/** @format */

import { AnalyticsService, UtilityService } from '@shared/data-access/services';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';

import { CompareBarProduct } from '@shared/data-access/interfaces';
import { CompareThumbnailComponent } from './compare-thumbnail/compare-thumbnail.component';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { ProductListCompareService } from '@plp/data-access/services';

@Component({
  selector: 'plp-components-compare-bar',
  imports: [CompareThumbnailComponent, HmfButtonComponent],
  templateUrl: './compare-bar.component.html',
  styleUrl: './compare-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompareBarComponent {
  private readonly compareService = inject(ProductListCompareService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly utilityService = inject(UtilityService);

  readonly compareBarContainer = viewChild<ElementRef<HTMLDivElement>>('compareBarContainer');

  readonly compareList = input<CompareBarProduct[]>([]);
  readonly showThumbnails = input(false);
  readonly actionButtonsSize = input<'small' | 'medium' | 'large'>('small');
  readonly tabPressed = output<void>();

  removeFromCompareList(
    event: { ecode: string | undefined; trigger: 'keyboard' | 'mouse' },
    index: number,
  ): void {
    if (event.trigger === 'mouse') {
      this.compareService.removeProductFromCompareList(event.ecode);
      return;
    }
    // If it's a keyboard event, we want to manage the focus after removing the item from the compare list for accessibility
    if (this.compareList().length === 1) {
      this.moveFocusAfterRemoveAllItems();
    } else {
      const buttonsNodeList: NodeListOf<HTMLButtonElement> | undefined =
        this.compareBarContainer()?.nativeElement?.querySelectorAll(
          `.compare-actions-container button`,
        );

      if (buttonsNodeList && buttonsNodeList.length > 0) {
        const buttonsArray: HTMLButtonElement[] = Array.from(buttonsNodeList);

        if (index + 1 < buttonsArray.length) {
          buttonsArray[index + 1].focus();
        }
      }
    }

    this.compareService.removeProductFromCompareList(event.ecode);
  }

  clearAllCompareList(): void {
    this.moveFocusAfterRemoveAllItems();
    this.compareService.clearCompareList();
  }

  moveFocusAfterRemoveAllItems(): void {
    if (this.showThumbnails()) {
      // desktop - to the first accordion header
      const firstAccordionHeader: HTMLElement | null = document.querySelector(
        '.plp-srlp-filters .hmf-accordion-label-and-control',
      );
      if (firstAccordionHeader) {
        firstAccordionHeader.focus();
      }
    } else {
      // mobile - to the first product card title
      const firstProductCardTitle: HTMLElement | null = document.querySelector(
        '.plp-card-container .product-title-link',
      );
      if (firstProductCardTitle) {
        firstProductCardTitle.focus();
      }
    }
  }

  compareProducts(): void {
    this.compareService.openCompareGrid();

    this.utilityService.yieldTask(() => {
      this.compareProductsAnalytics();
    });
  }

  private compareProductsAnalytics(): void {
    const compareAnalyticsData: {
      ModalName: string;
      ProductArray: { ProductID: string; CompareSource: string | undefined }[];
    } = {
      ModalName: 'Compare Products',
      ProductArray: [],
    };
    this.compareList().forEach((product) => {
      compareAnalyticsData.ProductArray.push({
        ProductID: product.ecode,
        CompareSource: product.compareSource,
      });
    });
    this.analyticsService.triggerAnalyticsEvent('CompareOpened', compareAnalyticsData);
  }
}
